import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Receipt, HandCoins, ArrowRight, Clock, CircleDot, CheckCircle2, Wallet } from "lucide-react";
import { RejectedNote } from "@/components/EditResubmit";

const rmbStages = ["Submitted", "PMEndorsed", "AccountsVerified", "Approved", "Paid"];
const advStages = ["Requested", "Approved", "Disbursed", "PartiallySettled", "Settled"];

const stageStyle = {
  Submitted: "bg-slate-100 text-slate-800 border-slate-300",
  PMEndorsed: "bg-blue-100 text-blue-800 border-blue-300",
  AccountsVerified: "bg-violet-100 text-violet-800 border-violet-300",
  Approved: "bg-emerald-100 text-emerald-800 border-emerald-300",
  Paid: "bg-emerald-600 text-white border-emerald-700",
  Rejected: "bg-red-100 text-red-800 border-red-300",
  Requested: "bg-slate-100 text-slate-800 border-slate-300",
  Disbursed: "bg-amber-100 text-amber-800 border-amber-300",
  PartiallySettled: "bg-orange-100 text-orange-800 border-orange-300",
  SentBack: "bg-rose-100 text-rose-800 border-rose-300",
  Settled: "bg-emerald-600 text-white border-emerald-700",
};

export default function MyRequests() {
  const { user } = useAuth();
  const [data, setData] = useState({ reimbursements: [], advances: [], totals: {} });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/my-requests");
      setData(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const rmbCount = data.reimbursements.length;
  const advCount = data.advances.length;
  const needsAction = [
    ...data.reimbursements.filter(r => r.status === "SentBack").map(r => ({ id: r.id, ref: r.rmb_id, inv: r.inv_ref, by: r.sent_back_by_name, role: r.sent_back_by_role, reason: r.sent_back_reason, link: `/app/reimbursements?edit=${r.id}` })),
    ...data.advances.filter(a => a.status === "SentBack").map(a => ({ id: a.id, ref: a.advance_id, by: a.sent_back_by_name, role: a.sent_back_by_role, reason: a.sent_back_reason, link: `/app/advances?edit=${a.id}` })),
    ...(data.sent_back_expenses || []).map(e => ({ id: e.id, ref: e.inv_ref || e.invoice_no, inv: e.inv_ref, by: e.sent_back_by_name, role: e.sent_back_by_role, reason: e.sent_back_reason, link: `/app/expenses-v2?edit=${e.id}` })),
  ];

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-medium">Workflow · My Activity</div>
        <h1 className="font-display text-3xl font-extrabold mt-1">My Requests</h1>
        <p className="text-sm text-slate-500 mt-1">Every reimbursement and advance you have raised. Sent-back requests can be edited and resubmitted; rejected ones are final and can be copied as a new claim.</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <Stat icon={Receipt} label="Reimbursements" value={rmbCount} testId="mr-stat-rmb" tone="violet" />
        <Stat icon={HandCoins} label="Advances" value={advCount} testId="mr-stat-adv" tone="amber" />
        <Stat icon={CheckCircle2} label="Approved / Paid" value={data.totals.approved_paid || 0} testId="mr-stat-approved" tone="emerald" />
        <Stat icon={CircleDot} label="In-flight" value={data.totals.in_flight || 0} testId="mr-stat-inflight" tone="slate" />
      </div>

      <Tabs defaultValue={(data.reimbursements || []).some(r => r.status === "SentBack") ? "action" : "reimbursements"}>
        <TabsList className="mb-4">
          <TabsTrigger value="action" data-testid="mr-tab-needs-action">Needs my action ({needsAction.length})</TabsTrigger>
          <TabsTrigger value="reimbursements" data-testid="mr-tab-reimbursements">Reimbursements ({rmbCount})</TabsTrigger>
          <TabsTrigger value="advances" data-testid="mr-tab-advances">Advances ({advCount})</TabsTrigger>
        </TabsList>

        <TabsContent value="action">
          {needsAction.length === 0 ? <Card className="border-dashed"><CardContent className="p-10 text-center text-sm text-slate-500">Nothing needs your action.</CardContent></Card> : (
            <div className="space-y-2">{needsAction.map(x => (
              <Card key={x.id} className="border-rose-300" data-testid={`mr-action-${x.ref}`}><CardContent className="p-3 flex items-center gap-3 flex-wrap">
                <span className="font-mono text-xs font-semibold">{x.ref}</span>{x.inv && <span className="font-mono text-xs text-indigo-700">{x.inv}</span>}
                <span className="text-sm text-rose-800 flex-1">Sent back by {x.by || "reviewer"}{x.role ? `, ${x.role}` : ""}: {x.reason}</span>
                <Link to={x.link}><Button size="sm" className="bg-rose-600 hover:bg-rose-700" data-testid={`mr-edit-${x.ref}`}>Edit & resubmit</Button></Link>
              </CardContent></Card>
            ))}</div>
          )}
        </TabsContent>

        <TabsContent value="reimbursements">
          <TrackerTable
            items={data.reimbursements}
            loading={loading}
            emptyLabel="You haven't raised any reimbursements yet."
            stages={rmbStages}
            kind="reimbursements"
            renderRow={r => ({
              key: r.id,
              raw: r,
              ref: r.rmb_id,
              amount: r.gross_amount,
              secondary: `${r.inv_ref ? r.inv_ref + " · " : ""}Payee: ${r.payee_name} · Vendor bill ${r.invoice_number || "—"}`,
              status: r.status === "Approved" && r.payment_status === "Processed" ? "Paid" : r.status,
              label: r.status_label,
              paymentStatus: r.payment_status,
              createdAt: r.created_at,
              expenseMonth: r.expense_month,
              link: "/app/reimbursements",
              testId: `mr-rmb-${r.rmb_id}`,
            })}
          />
        </TabsContent>

        <TabsContent value="advances">
          <TrackerTable
            items={data.advances}
            loading={loading}
            emptyLabel="You haven't raised any advances yet."
            stages={advStages}
            kind="advances"
            renderRow={a => ({
              key: a.id,
              raw: a,
              ref: a.advance_id,
              amount: a.amount,
              secondary: `${a.purpose?.slice(0, 60) || "—"}${(a.purpose?.length || 0) > 60 ? "…" : ""}`,
              status: a.status,
              label: a.status_label,
              paymentStatus: ["Disbursed", "PartiallySettled"].includes(a.status) ? `Outstanding ₹ ${(a.outstanding || 0).toLocaleString()} · ${Math.max(0, 30 - Math.floor((Date.now() - new Date(a.disbursed_at || a.created_at).getTime()) / 86400000))} days left to settle` : "",
              settleLink: ["Disbursed", "PartiallySettled"].includes(a.status) ? `/app/advances?settle=${a.id}` : null,
              createdAt: a.created_at,
              expenseMonth: null,
              link: "/app/advances",
              testId: `mr-adv-${a.advance_id}`,
            })}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function TrackerTable({ items, loading, emptyLabel, stages, renderRow, kind }) {
  const rows = items.map(renderRow);
  if (loading) return <Card><CardContent className="p-10 text-center text-sm text-slate-500">Loading…</CardContent></Card>;
  if (rows.length === 0) return (
    <Card className="border-dashed"><CardContent className="p-14 text-center">
      <Wallet className="w-8 h-8 mx-auto mb-2 text-slate-300" />
      <div className="text-sm text-slate-600 font-medium">{emptyLabel}</div>
    </CardContent></Card>
  );
  return (
    <Card>
      <CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase text-slate-500 bg-slate-50">
            <tr className="border-b">
              <th className="text-left px-3 py-2">Reference</th>
              <th className="text-left px-3 py-2">Details</th>
              <th className="text-right px-3 py-2">Amount</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Progress</th>
              <th className="text-right px-3 py-2">Age</th>
              <th className="text-right px-3 py-2 min-w-[180px]"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => {
              const isRejected = row.status === "Rejected" || row.raw.status === "Rejected";
              const currIdx = stages.indexOf(row.status);
              const age = ageDays(row.createdAt);
              const sentBack = row.raw.status === "SentBack";
              return (
                <tr key={row.key} className={`border-b border-slate-100 hover:bg-slate-50 ${isRejected ? "bg-red-50/30" : ""}`} data-testid={row.testId}>
                  <td className="px-3 py-3 font-mono text-xs font-semibold">
                    {row.ref}
                  </td>
                  <td className="px-3 py-3">
                    <div className="text-xs text-slate-600">{row.secondary}</div>
                    {row.expenseMonth && <div className="text-[10px] font-mono text-slate-500 mt-0.5">Month {row.expenseMonth}</div>}
                    {isRejected && <div className="mt-1"><RejectedNote doc={row.raw} copyLink={`/app/${kind}?copy=${row.raw.id}`} testId={`${row.testId}-rejected`} /></div>}
                  </td>
                  <td className="px-3 py-3 text-right font-mono font-bold text-emerald-700">₹ {(row.amount || 0).toLocaleString()}</td>
                  <td className="px-3 py-3">
                    <Badge variant="outline" className={`text-[10px] uppercase font-semibold border ${stageStyle[isRejected ? "Rejected" : row.status] || ""}`}>{isRejected ? "Rejected" : (row.label || row.status)}</Badge>
                    {row.paymentStatus && <div className="text-[10px] text-slate-500 mt-1">{row.paymentStatus}</div>}
                    {row.settleLink && <Link to={row.settleLink} className="text-[11px] font-semibold text-blue-700" data-testid={`mr-settle-${row.ref}`}>Settle →</Link>}
                  </td>
                  <td className="px-3 py-3">
                    <StageBar stages={stages} currIdx={isRejected ? -1 : currIdx} rejected={isRejected} />
                  </td>
                  <td className="px-3 py-3 text-right text-xs text-slate-500 font-mono"><Clock className="w-3 h-3 inline mr-1" />{age}d</td>
                  <td className="px-3 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {sentBack && (
                        <Link to={`/app/${kind}?edit=${row.raw.id}`}><Button size="sm" className="h-8 px-2.5 bg-rose-600 hover:bg-rose-700" data-testid={`${row.testId}-edit`}>Edit & resubmit</Button></Link>
                      )}
                      <Link to={row.link}><Button size="sm" variant="ghost" data-testid={`${row.testId}-open`}>Open <ArrowRight className="w-3 h-3 ml-1" /></Button></Link>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function StageBar({ stages, currIdx, rejected }) {
  return (
    <div className="flex items-center gap-0.5">
      {stages.map((s, i) => {
        const done = !rejected && i <= currIdx;
        const cls = rejected ? "bg-red-200" : done ? "bg-emerald-500" : "bg-slate-200";
        return <div key={s} className={`h-1.5 flex-1 min-w-[16px] ${cls} ${i === 0 ? "rounded-l" : ""} ${i === stages.length - 1 ? "rounded-r" : ""}`} title={s} />;
      })}
    </div>
  );
}

function Stat({ icon: Icon, label, value, tone, testId }) {
  const toneCls = tone === "violet" ? "text-violet-700" : tone === "amber" ? "text-amber-700" : tone === "emerald" ? "text-emerald-700" : "text-slate-900";
  return (
    <Card><CardContent className="p-4" data-testid={testId}>
      <div className="text-xs uppercase text-slate-500 flex items-center gap-1"><Icon className="w-3 h-3" /> {label}</div>
      <div className={`font-display font-bold text-2xl mt-1 ${toneCls}`}>{value}</div>
    </CardContent></Card>
  );
}

function ageDays(iso) {
  if (!iso) return 0;
  try { return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000)); }
  catch { return 0; }
}
